import { useQuery } from "@apollo/client/react";import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Searcher from './Searcher';
import { generateColumnsDefaultConfiguration } from '../../../../../../components/dataGrid/columnHelpers';
import { adaptReclamosParsedArray, GET_RECLAMOS_WITHOUT_FILTERS } from '../../../../../../data/graphQL/queries/Reclamos/getReclamosWithoutFilters';
import { GET_ALL_AREAS_RECLAMO } from '../../../../../../data/graphQL/queries/Reclamos/getAllAreasReclamo';
import { GET_ALL_CLASIFICACIONES_IR } from '../../../../../../data/graphQL/queries/Reclamos/getAllClasificacionesIR';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import { CustomChip } from '../../../../../../components/chip/Chip';
import { adaptDelegacionArray } from '../../../../../../adapters/delegacionAdapter';

const storageId = sessionStorage.getItem('idUsuario');
const TIPO_RECLAMO = 1;
const TIPO_RECLAMO_EFI = 4;

const ReclamoField = ({
  onChange,
  idGestionReclamo,
  tipoReclamo,
  disabled,
  idDelegacion
}) => {
  const [areasReclamo, setAreasReclamo] = useState([]);
  const [clasificacionesIR, setClasificacionesIR] = useState([]);
  const [delegaciones, setDelegaciones] = useState([]);
  const ESTADO_PE = 'Pendiente';
  const ESTADO_AP = 'Aprobado';
  const ESTADO_RE = 'Rechazado';
  const ESTADO_DE = 'Desestimado';
  const ESTADO_CE = 'Cerrado';

  const chipConfig = {
    [ESTADO_PE]: { isYellow: true, title: ESTADO_PE },
    [ESTADO_AP]: { isGreen: true, title: ESTADO_AP },
    [ESTADO_RE]: { isViolet: true, title: ESTADO_RE },
    [ESTADO_CE]: { isGreen: true, title: ESTADO_CE },
    [ESTADO_DE]: { isRed: true, title: ESTADO_DE }
  };

  const getChipEstado = (estado) => {
    const config = chipConfig[estado];
    if (config) {
      return <CustomChip
        label={config.title}
        isYellow={config.isYellow}
        isViolet={config.isViolet}
        isGreen={config.isGreen}
        isGray={config.isGray}
        isRed={config.isRed}
        title={config.title} />;

    }
    return null;
  };

  const { data: dataDelegacionesUsuario, loading: loadingDelegaciones} = useQuery(GET_DELEGACIONES_BY_USUARIO, {
    variables: { idUsuario: JSON.parse(storageId, 10) }
  });

  useEffect(() => {
    if (!dataDelegacionesUsuario || loadingDelegaciones) return;
    setDelegaciones(adaptDelegacionArray(dataDelegacionesUsuario));
  }, [dataDelegacionesUsuario]);

  const { data: dataAreas, loading: loadingAreas } = useQuery(GET_ALL_AREAS_RECLAMO);

  useEffect(() => {
    if (!dataAreas || loadingAreas) return;
    const areas = dataAreas?.allAreasReclamo?.map((area) => ({ id: area.idAreaReclamo, displayName: area.descripcion }));
    setAreasReclamo(areas);
  }, [dataAreas]);

  const { data: dataClasificaciones } = useQuery(GET_ALL_CLASIFICACIONES_IR);

  useEffect(() => {
    if (!dataClasificaciones) return;
    const clasificaciones = dataClasificaciones?.allClasificacionReclamo?.map((clasif) => ({ id: clasif.idClasificacionReclamo, displayName: clasif.descripcion }));
    setClasificacionesIR(clasificaciones);
  }, [dataClasificaciones]);

  const columns = [
  {
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    field: 'id',
    headerName: '#'
  },
  {
    width: 170,
    field: 'idDelegacion',
    headerName: 'Delegación',
    valueGetter: (idDelegacion) => {
      return delegaciones.find((delegacion) => delegacion.id === idDelegacion)?.displayName || '-';
    }
  },
  {
    width: 200,
    field: 'idAreaReclamo',
    headerName: 'Área responsable',
    valueGetter: (idAreaReclamo) => {
      return areasReclamo.find((area) => area.id === idAreaReclamo)?.displayName || '-';
    }
  },
  {
    width: 80,
    field: 'estado',
    headerName: 'Estado',
    headerAlign: 'center',
    align: 'center',
    renderCell: (cellProps) => {
      const { row } = cellProps;
      return getChipEstado(row.estado);
    },
    sortable: false
  },
  {
    width: 120,
    field: 'idClasificacionReclamo',
    headerName: 'Clasificación IR',
    valueGetter: (idClasificacionReclamo) => {
      return clasificacionesIR.find((clasif) => clasif.id === idClasificacionReclamo)?.displayName || '-';
    }
  },
  {
    width: 80,
    field: 'fechaRegistro',
    headerName: 'F. de registro'
  },
  {
    width: 180,
    field: 'paciente',
    headerName: 'Paciente',
    sortable: false
  },
  {
    width: 150,
    field: 'financiador',
    headerName: 'Financiador'
  }];


  const CONFIGURATED_COLUMNS = useMemo(() => generateColumnsDefaultConfiguration(columns, true), [columns, delegaciones, areasReclamo, clasificacionesIR, idGestionReclamo]);

  const getSearchParams = useCallback((textValue) => {
    const filterDelegacion = {
      idDelegacion: { eq: idDelegacion },
      idTipoReclamo: { eq: tipoReclamo === TIPO_RECLAMO ? 1 : 2 },
      idEstadoReclamo: { neq: 4 }
    };

    const searchParams = {
      and: [filterDelegacion]
    };

    if (textValue) {
      searchParams.and.push({
        or: [
        parseInt(textValue) > 0 ? { numeroReclamo: { eq: parseInt(textValue) } } : {},
        { idPacienteNavigation: { nombre: { contains: textValue } } },
        { idPacienteNavigation: { apellido: { contains: textValue } } }]

      });
    }

    return searchParams;
  }, [idDelegacion, tipoReclamo]);

  const handleRowClick = (row) => {
    const value = !!row ? row.idGestionReclamo : null;
    onChange('idGestionReclamo', value);
  };

  const getEntityValue = (row) => {
    const value = !!row ? row.id : null;
    return `${value}`;
  };

  return <Searcher
    displayName={tipoReclamo === TIPO_RECLAMO ? 'Número de Reclamo' : tipoReclamo === TIPO_RECLAMO_EFI ? 'Número de EFI' : ""}
    titleModal={'Seleccionar ' + (tipoReclamo === TIPO_RECLAMO ? 'Reclamo' : 'EFI')}
    entityName='allReclamosWithoutFilters'
    entityId='idGestionReclamo'
    entityIdValue={idGestionReclamo}
    columns={CONFIGURATED_COLUMNS}
    query={GET_RECLAMOS_WITHOUT_FILTERS}
    adapter={adaptReclamosParsedArray}
    getSearchParams={getSearchParams}
    onRowClick={handleRowClick}
    getEntityValue={getEntityValue}
    disabled={disabled} />;

};

export default ReclamoField;